import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Plus, Trash2, Loader2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { CONNECTOR_META, emptyTask } from "./shared";

export default function GovernanceModal({ onClose }) {
  const [tab, setTab] = useState("sod");
  const [rules, setRules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [rule, setRule] = useState({ name: "", left: "", right: "", severity: "high", detail: "" });
  const [tmpl, setTmpl] = useState({ name: "", description: "", tasks: [emptyTask()] });
  const [busy, setBusy] = useState(false);

  const loadRules = () => api.get("/orchestration/sod-rules").then(({ data }) => setRules(data)).catch((e) => toast.error(apiErr(e)));
  const loadTemplates = () => api.get("/orchestration/templates").then(({ data }) => setTemplates(data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { loadRules(); loadTemplates(); }, []);

  const addRule = async () => {
    if (!rule.name.trim() || !rule.left.trim() || !rule.right.trim()) { toast.error("Name and both conflicting terms are required."); return; }
    setBusy(true);
    try { await api.post("/orchestration/sod-rules", rule); toast.success("Rule added."); setRule({ name: "", left: "", right: "", severity: "high", detail: "" }); loadRules(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const delRule = async (id) => {
    try { await api.delete(`/orchestration/sod-rules/${id}`); toast.success("Rule removed."); loadRules(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const setTTask = (i, k, v) => setTmpl((s) => ({ ...s, tasks: s.tasks.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)) }));
  const addTTask = () => setTmpl((s) => ({ ...s, tasks: [...s.tasks, emptyTask()] }));
  const delTTask = (i) => setTmpl((s) => ({ ...s, tasks: s.tasks.filter((_, idx) => idx !== i) }));
  const addTemplate = async () => {
    if (!tmpl.name.trim()) { toast.error("Template name is required."); return; }
    const clean = tmpl.tasks.filter((t) => t.app_name.trim());
    if (!clean.length) { toast.error("Add at least one app to the template."); return; }
    setBusy(true);
    try { await api.post("/orchestration/templates", { name: tmpl.name, description: tmpl.description, tasks: clean }); toast.success("Template saved."); setTmpl({ name: "", description: "", tasks: [emptyTask()] }); loadTemplates(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const delTemplate = async (id) => {
    try { await api.delete(`/orchestration/templates/${id}`); toast.success("Template removed."); loadTemplates(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="governance-modal">
      <div className="bg-[var(--parchment)] rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="brand-band text-white px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          <p className="font-semibold">Governance — SoD rules &amp; role templates</p>
          <button onClick={onClose} className="text-white/80 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex items-center gap-1 px-6 pt-4 border-b border-[hsl(var(--border))]">
          <button data-testid="gov-tab-sod" onClick={() => setTab("sod")} className={`px-3 py-2 text-sm font-semibold -mb-px border-b-2 ${tab === "sod" ? "border-[var(--burgundy)] text-[var(--burgundy)]" : "border-transparent text-neutral-500"}`}>SoD rules</button>
          <button data-testid="gov-tab-templates" onClick={() => setTab("templates")} className={`px-3 py-2 text-sm font-semibold -mb-px border-b-2 ${tab === "templates" ? "border-[var(--burgundy)] text-[var(--burgundy)]" : "border-transparent text-neutral-500"}`}>Role templates</button>
        </div>

        <div className="p-6 space-y-4">
          {tab === "sod" ? (
            <>
              <div className="space-y-2" data-testid="sod-rules-list">
                {rules.map((r) => (
                  <div key={r.id} className="card-surface p-3 flex items-center justify-between gap-2 text-sm">
                    <div>
                      <p className="font-medium text-neutral-800">{r.name} <span className="text-[10px] uppercase px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 ml-1">{r.severity}</span></p>
                      <p className="text-xs text-neutral-500">"{r.left}" conflicts with "{r.right}"{r.detail ? ` — ${r.detail}` : ""}</p>
                    </div>
                    <button data-testid={`del-rule-${r.id}`} onClick={() => delRule(r.id)} className="text-neutral-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                {!rules.length && <p className="text-sm text-neutral-400 text-center py-4">No SoD rules.</p>}
              </div>
              <div className="card-surface p-4 space-y-2">
                <p className="text-xs font-semibold text-neutral-600">Add a rule (flags a conflict when a job holds both terms across its tasks)</p>
                <input value={rule.name} onChange={(e) => setRule({ ...rule, name: e.target.value })} placeholder="Rule name (e.g. Vendor create vs pay)" className="field-input" data-testid="rule-name-input" />
                <div className="grid grid-cols-2 gap-2">
                  <input value={rule.left} onChange={(e) => setRule({ ...rule, left: e.target.value })} placeholder="Term A (e.g. vendor-create)" className="field-input" data-testid="rule-left-input" />
                  <input value={rule.right} onChange={(e) => setRule({ ...rule, right: e.target.value })} placeholder="Term B (e.g. vendor-pay)" className="field-input" data-testid="rule-right-input" />
                </div>
                <div className="flex items-center gap-2">
                  <select value={rule.severity} onChange={(e) => setRule({ ...rule, severity: e.target.value })} className="field-input !w-auto">
                    <option value="high">high</option><option value="medium">medium</option><option value="low">low</option>
                  </select>
                  <input value={rule.detail} onChange={(e) => setRule({ ...rule, detail: e.target.value })} placeholder="Why they conflict (optional)" className="field-input flex-1" />
                  <button data-testid="add-rule-btn" onClick={addRule} disabled={busy} className="btn btn-primary">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add"}</button>
                </div>
                <p className="text-[11px] text-neutral-400">Only <b>high</b>-severity conflicts block "Run all" until overridden.</p>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-2" data-testid="gov-templates-list">
                {templates.map((t) => (
                  <div key={t.id} className="card-surface p-3 flex items-center justify-between gap-2 text-sm">
                    <div>
                      <p className="font-medium text-neutral-800">{t.name} <span className="text-xs text-neutral-400">· {t.tasks.length} app(s)</span></p>
                      {t.description && <p className="text-xs text-neutral-500">{t.description}</p>}
                    </div>
                    <button data-testid={`del-template-${t.id}`} onClick={() => delTemplate(t.id)} className="text-neutral-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                {!templates.length && <p className="text-sm text-neutral-400 text-center py-4">No templates.</p>}
              </div>
              <div className="card-surface p-4 space-y-2">
                <p className="text-xs font-semibold text-neutral-600">New role template</p>
                <input value={tmpl.name} onChange={(e) => setTmpl({ ...tmpl, name: e.target.value })} placeholder="Template name (e.g. Branch Manager)" className="field-input" data-testid="tmpl-name-input" />
                <input value={tmpl.description} onChange={(e) => setTmpl({ ...tmpl, description: e.target.value })} placeholder="Description (optional)" className="field-input" />
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-neutral-600">Apps</span>
                  <button onClick={addTTask} className="text-xs text-[var(--burgundy)] font-semibold flex items-center gap-1"><Plus className="w-3 h-3" /> Add app</button>
                </div>
                {tmpl.tasks.map((t, i) => (
                  <div key={i} className="border border-[hsl(var(--border))] rounded-lg p-2 space-y-1.5" data-testid={`tmpl-task-${i}`}>
                    <div className="grid grid-cols-[1fr_1fr_auto] gap-1.5 items-center">
                      <input value={t.app_name} onChange={(e) => setTTask(i, "app_name", e.target.value)} placeholder="App name" className="field-input" data-testid={`tmpl-app-${i}`} />
                      <select value={t.connector} onChange={(e) => setTTask(i, "connector", e.target.value)} className="field-input">
                        {Object.entries(CONNECTOR_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                      </select>
                      <button onClick={() => delTTask(i)} className="text-neutral-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="grid grid-cols-[1fr_auto_auto] gap-1.5 items-center">
                      <input value={t.target} onChange={(e) => setTTask(i, "target", e.target.value)} placeholder="Group / role / entitlement" className="field-input text-xs" />
                      <label className="text-xs text-neutral-600 inline-flex items-center gap-1"><input type="checkbox" checked={t.requires_approval} onChange={(e) => setTTask(i, "requires_approval", e.target.checked)} /> Appr.</label>
                      <label className="text-xs text-neutral-600 inline-flex items-center gap-1"><input type="checkbox" checked={t.requires_mfa} onChange={(e) => setTTask(i, "requires_mfa", e.target.checked)} /> MFA</label>
                    </div>
                    {t.requires_approval && <input value={t.approver} onChange={(e) => setTTask(i, "approver", e.target.value)} placeholder="Approver (email or role)" className="field-input text-xs" />}
                  </div>
                ))}
                <div className="flex justify-end">
                  <button data-testid="add-template-btn" onClick={addTemplate} disabled={busy} className="btn btn-primary">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save template"}</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
